import { useMemo } from 'react';
import { Merge } from 'ts-essentials';

import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';

import {
   createSingleInterpolatedTranslationKey,
   practitionerNameFormatter,
   useFormController,
   useTranslate,
} from '@dreryk/react-common';
import { FormController, StyledFlex, TextView } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientSimpleCreateAction } from '~libs.desktop.shared.patient-simple-create-action';
import { PatientGroupTags } from '~libs.desktop.patient-groups';

import receptionService from '../../../../receptionService';
import { appointmentsTranslationKeys, waitlistEntryTranslationKeys } from '../../../../translations';
import {
   createIncompatiblePreferenceNotificationChecker,
   createPriorityPreferenceChecker,
} from '../../create/waitlist/AppointmentWaitlistCreateForm.utils';
import { IncompatiblePreferenceNotification } from '../../create/waitlist/IncompatiblePreferenceNotification';

import {
   AppointmentTimeSlotOccupiedErrorHandlerProps,
   useAppointmentTimeSlotOccupiedErrorHandler,
} from './AppointmentForm.hooks';
import { StyledAppointmentForm, StyledWarnings } from './AppointmentForm.styled';
import { AppointmentFormBean, AppointmentFormController } from './AppointmentForm.types';
import {
   healthcareProvidingLocationViewVisitor,
   isAvailabilityRangeTimeValid,
   patientCreateAvailable,
   practitionerViewVisitor,
   recurrencePeriodVisitor,
   verifyModeVisitor,
} from './AppointmentForm.utils';
import { AppointmentAvailabilityStatus } from './components/AppointmentAvailabilityStatus';
import { AppointmentDateTimeRangePicker } from './components/AppointmentDateTimeRangePicker';
import { AppointmentNote } from './components/AppointmentNote';
import { AppointmentsCountInputField } from './fields/AppointmentsCountInputField';
import { HealthcareProviderLocationSelect } from './fields/healthcareProviderLocation/HealthcareProviderLocationSelect';
import { HealthcareProviderLocationTextView } from './fields/healthcareProviderLocation/HealthcareProviderLocationTextView';
import { OverlappingStrategyField } from './fields/OverlappingStrategyField';
import { PatientAutosuggest } from './fields/patient/PatientAutosuggest';
import { PractitionerSelect } from './fields/PractitionerSelect/PractitionerSelect';
import { RecurrencePeriodField } from './fields/RecurrencePeriodField';
import { RecurrencePeriodUnitField } from './fields/RecurrencePeriodUnitField';
import { RecurrenceToggleField } from './fields/RecurrenceToggleField';
import { RecurrenceTypeField } from './fields/RecurrenceTypeField';
import { DateConfirmedWithPatientField } from './fields/DateConfirmedWithPatientField';
import { EstimatedDurationField } from './fields/EstimatedDurationField';
import { WeekdayPickerField } from './fields/WeekdayPickerField';
import { VideoConsultationCheckbox } from './fields/VideoConsultationCheckbox';
import { VideoConsultationWarning } from './components/VideoConsultationWarning';
import { ServiceLocationWarning } from './components/ServiceLocationWarning';
import { AdvancePaymentMayRequiredAnAdditionalPaymentWarning } from './components/AdvancePaymentMayRequiredAnAdditionalPaymentWarning';
import { VisitRequiredAdvancePaymentInfo } from './components/VisitRequiredAdvancePaymentInfo';

type AppointmentFormRenderRenderProps = Merge<
   Pick<AppointmentFormController, 'handleSubmit' | 'formState' | 'trigger' | 'getValue'>,
   AppointmentTimeSlotOccupiedErrorHandlerProps
>;
type AppointmentFormRenderRenderer = (renderProps: AppointmentFormRenderRenderProps) => JSX.Element;

type AppointmentFormInitializer = () => AppointmentFormBean;

type AppointmentFormProps = {
   readonly children: AppointmentFormRenderRenderer;
   readonly initializer: AppointmentFormInitializer;
   readonly appointmentId?: number;
   readonly practitioners: AppointmentPractitioner[];
   readonly healthcareProviderLocations: HealthcareProviderLocation[];
   readonly overlappingAppointmentsAllowed?: boolean;
   readonly patientCreateAllowed?: boolean;
   readonly showDateConfirmedWithPatientField?: boolean;
   readonly waitlistEntry?: WaitlistEntry | null;
   readonly onlinePaymentsIntegrationExists: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentForm = (props: AppointmentFormProps): JSX.Element => {
   const translate = useTranslate();
   const { register, handleSubmit, watch, setValue, trigger, control, formState, getValue, setError, sync } =
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useFormController<AppointmentFormBean>(useMemo(() => props.initializer(), [props.initializer]));
   const bean = watch();
   const shouldShowIncompatiblePreferenceNotification = createIncompatiblePreferenceNotificationChecker(
      bean,
      props.waitlistEntry ?? null
   );
   const isPriorityPreference = props.waitlistEntry
      ? createPriorityPreferenceChecker(props.waitlistEntry)
      : () => false;

   return (
      <>
         <StyledAppointmentForm noValidate autoComplete={'off'}>
            {healthcareProvidingLocationViewVisitor(
               props.healthcareProviderLocations,
               bean.healthcareProviderLocation,
               formState.touched.healthcareProviderLocation
            )({
               text: ({ editable }) => <HealthcareProviderLocationTextView register={register} editable={editable} />,
               select: () => (
                  <HealthcareProviderLocationSelect
                     register={register}
                     healthcareProviderLocations={props.healthcareProviderLocations}
                  />
               ),
            })}
            {shouldShowIncompatiblePreferenceNotification('location') ? (
               <IncompatiblePreferenceNotification
                  testId={'incompatibleLocation'}
                  label={createSingleInterpolatedTranslationKey(
                     isPriorityPreference('location')
                        ? waitlistEntryTranslationKeys.priorityPreference
                        : waitlistEntryTranslationKeys.preference,
                     'label',
                     props.waitlistEntry!.location!.name
                  )}
                  warning={isPriorityPreference('location')}
               />
            ) : null}
            {protectFeature('bookingVideoConsultations', <VideoConsultationCheckbox register={register} />)}
            {shouldShowIncompatiblePreferenceNotification('encounterMode') ? (
               <IncompatiblePreferenceNotification
                  testId={'incompatibleEncounterMode'}
                  label={createSingleInterpolatedTranslationKey(
                     waitlistEntryTranslationKeys.preference,
                     'label',
                     translate(waitlistEntryTranslationKeys.encounterModeMap[props.waitlistEntry!.encounterMode])
                  )}
               />
            ) : null}
            {practitionerViewVisitor(props.practitioners)({
               single: () => (
                  <FormController<AppointmentPractitioner> controller={register('practitioner')}>
                     {({ value: practitioner }) => (
                        <TextView label={appointmentsTranslationKeys.specialist} testId={'specialist'}>
                           <PractitionerSafePresenter
                              testId={'practitioner'}
                              practitioner={practitioner}
                              type={'text'}
                              withSpecialties
                           />
                        </TextView>
                     )}
                  </FormController>
               ),
               multiple: () => <PractitionerSelect register={register} practitioners={props.practitioners} />,
            })}
            {shouldShowIncompatiblePreferenceNotification('practitioner') ? (
               <IncompatiblePreferenceNotification
                  testId={'incompatiblePractitioner'}
                  label={createSingleInterpolatedTranslationKey(
                     isPriorityPreference('practitioner')
                        ? waitlistEntryTranslationKeys.priorityPreference
                        : waitlistEntryTranslationKeys.preference,
                     'label',
                     practitionerNameFormatter(props.waitlistEntry!.practitioner, {
                        options: { order: 'firstLast' },
                        translator: translate,
                     })!
                  )}
                  warning={isPriorityPreference('practitioner')}
               />
            ) : null}
            <StyledFlex gap={8} direction={'column'}>
               <StyledFlex gap={'24px'}>
                  <PatientAutosuggest register={register} name={'patient'} disabled={!props.patientCreateAllowed} />
                  {patientCreateAvailable(bean.patient, props.patientCreateAllowed) ? (
                     <PatientSimpleCreateAction
                        onSuccess={(patient) => {
                           setValue('patient', patient);
                           trigger('patient');
                        }}
                     />
                  ) : null}
               </StyledFlex>
               <PatientGroupTags patient={bean.patient} />
            </StyledFlex>
            <StyledFlex gap={'24px'}>
               <AppointmentDateTimeRangePicker
                  control={control}
                  appointmentId={props.appointmentId}
                  formState={formState}
                  setValue={setValue}
                  register={register}
                  getValue={getValue}
                  trigger={trigger}
                  setError={setError}
                  watch={watch}
               />

               <EstimatedDurationField register={register} watch={watch} setValue={setValue} />
            </StyledFlex>
            {verifyModeVisitor(props.appointmentId)({
               creation: () => (
                  <StyledFlex>
                     <RecurrenceToggleField register={register} />
                  </StyledFlex>
               ),
            })}
            {verifyModeVisitor(props.appointmentId)({
               edition: () =>
                  isAvailabilityRangeTimeValid(bean.date, bean.startTime, bean.endTime) && (
                     <AppointmentAvailabilityStatus
                        practitionerId={bean.practitioner.id}
                        date={bean.date}
                        startTime={bean.startTime}
                        endTime={bean.endTime}
                     />
                  ),
               creation: () =>
                  Boolean(bean.recurrence) && (
                     <>
                        <StyledFlex gap={'24px'} alignItems={'flex-start'}>
                           <StyledFlex gap={'8px'} alignItems={'flex-end'}>
                              <RecurrencePeriodField register={register} recurrenceUnit={bean.recurrencePeriodUnit} />
                              <RecurrencePeriodUnitField register={register} setValue={setValue} />
                           </StyledFlex>
                           {recurrencePeriodVisitor(bean)({
                              forMonth: () => <RecurrenceTypeField register={register} date={bean.date} />,
                              forWeek: () => (
                                 <WeekdayPickerField register={register} setValue={setValue} date={bean.date} />
                              ),
                           })}
                           <AppointmentsCountInputField register={register} />
                        </StyledFlex>
                        {props.overlappingAppointmentsAllowed ? <OverlappingStrategyField register={register} /> : null}
                     </>
                  ),
            })}
            <AppointmentNote register={register} note={bean.note} />
            <receptionService.calendar.appointment.ReceptionServiceInteractiveTable
               practitionerId={bean.practitioner.id}
               register={register}
               control={control}
               sync={sync}
               setValue={setValue}
            />
            {shouldShowIncompatiblePreferenceNotification('service') ? (
               <IncompatiblePreferenceNotification
                  testId={'incompatibleServices'}
                  label={createSingleInterpolatedTranslationKey(
                     waitlistEntryTranslationKeys.preference,
                     'label',
                     props.waitlistEntry!.receptionService!.name
                  )}
                  warning
               />
            ) : null}
            {props.showDateConfirmedWithPatientField ? <DateConfirmedWithPatientField register={register} /> : null}
            <StyledWarnings>
               {protectFeature(
                  'bookingVideoConsultations',
                  <VideoConsultationWarning watch={watch} control={control} />
               )}
               <ServiceLocationWarning watch={watch} control={control} />
               {protectFeature(
                  'searchingPrivateMedicalServices',
                  props.onlinePaymentsIntegrationExists
                     ? verifyModeVisitor(props.appointmentId)({
                          creation: () => <VisitRequiredAdvancePaymentInfo watch={watch} control={control} />,
                          edition: () => (
                             <AdvancePaymentMayRequiredAnAdditionalPaymentWarning watch={watch} control={control} />
                          ),
                       })
                     : null
               )}
            </StyledWarnings>
         </StyledAppointmentForm>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
            setAppointmentTimeSlotOccupiedError: useAppointmentTimeSlotOccupiedErrorHandler(setError),
            getValue: getValue,
            trigger: trigger,
         })}
      </>
   );
};
